import React, { useEffect, useState } from "react";
import { Alert, Badge, Card, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { GamePublicationState, GamePublicationWishlistState, useChoiceLabel } from "@/const";
import { useRequest } from "@components/AppHook";
import MarketSerialBatchStateDisplay from "@components/Displays/MarketSerialBatchStateDisplay";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal, OverflowArea } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { getAttachement, url_reverse } from "@components/Utils";

export default function Serials(props) {
    const params = useParams();
    if (params.action) {
        if (params.action === "generation") {
            return <BatchGeneration {...props} />;
        } else if (params.action === "ban") {
            props = { ...props, batchId: params.action };
            return <Ban {...props} />;
        }
    }
    return <BatchList {...props} />;
}

function BatchList({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, apiUrl } = useAppApi(url_reverse("api:game:serials:batch_list", { game_id: gameId }));
    const commonInfo = useRouteLoaderData("commonInfo");
    const { MarketSerialBatchSerialType } = commonInfo.const_choices;
    const MarketSerialBatchSerialTypeTrans = useChoiceLabel("MarketSerialBatchSerialType");
    const [serialSearch, setSerialSearch] = useState({});
    const { api_get } = useRequest();

    if (!apiData) return;

    const doSerialSearch = (ev) => {
        api_get(ev, apiUrl, {
            params: { serial_search: serialSearch.val },
            then: (resp) => {
                if (resp.status === "ok") {
                    setSerialSearch((prev) => ({ ...prev, info: resp }));
                }
            },
        });
    };

    const headerTools = (
        <>
            <DevForm.Button
                variant="dark"
                size="sm"
                name="serial_search_modal"
                onClick={() => setSerialSearch({ show: true })}
            >
                {t("Поиск ключа")}
            </DevForm.Button>{" "}
            <Link to={url_reverse("games:serials:ban", { game_id: gameId })} className="btn btn-sm btn-dark">
                {t("Бан")}
            </Link>{" "}
            <Link to={url_reverse("games:serials:generation", { game_id: gameId })} className="btn btn-sm btn-dark">
                {t("Генерация")}
            </Link>
        </>
    );

    const stat = apiData.keys_count_stat;
    return (
        <ListingCard header={header} headerTools={headerTools}>
            <DevModal
                size="lg"
                show={serialSearch.show}
                onHide={() => setSerialSearch({})}
                header={t("Поиск ключа")}
                body={
                    <>
                        <FormFieldGroup label={t("Серийный номер")}>
                            <DevForm.TextInput
                                value={serialSearch.val}
                                onChange={(ev) => setSerialSearch((prev) => ({ ...prev, val: ev.target.value }))}
                            />
                        </FormFieldGroup>
                        {serialSearch.info &&
                            (serialSearch.info.batch_id ? (
                                <Alert variant="info">
                                    {t("ID партии")}: {serialSearch.info.batch_id}
                                    <br />
                                    {t("Дата активации")}: {serialSearch.info.date_serial || t("Не активирован")}
                                    <br />
                                    {commonInfo.account.is_super && (
                                        <>
                                            {t("Логин владельца")}: {serialSearch.info.email || "-"}
                                            <br />
                                        </>
                                    )}
                                    {serialSearch.info.is_banned && <div className="text-danger">{t("Забанен")}</div>}
                                </Alert>
                            ) : (
                                <Alert variant="danger">{t("Не найден")}</Alert>
                            ))}
                    </>
                }
                footer={
                    <DevForm.Button variant="primary" name="serial_search" onClick={doSerialSearch}>
                        {t("Найти")}
                    </DevForm.Button>
                }
            />

            <div>
                <Badge variant="success">
                    {t("Ключи для раздачи")}: {stat.dist.count} / {stat.dist.limit}
                </Badge>{" "}
                <Badge variant="success">
                    {t("Маркетинговые ключи VK Play")}: {stat.vkplay.count} / {stat.vkplay.limit}
                </Badge>
            </div>

            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th width="12%">{t("Дата генерации")}</th>
                        <th width="12%">{t("Срок действия")}</th>
                        <th>{t("ID партии")}</th>
                        <th>{t("Тип ключей")}</th>
                        <th>{t("Количество")}</th>
                        <th>{t("Комментарий")}</th>
                        <th>{t("Логин")}</th>
                        <th>{t("Статус")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((obj) => {
                        const vkplay_batch = obj.serial_type === MarketSerialBatchSerialType.VKPLAY[0];
                        return (
                            <tr key={obj.id} className={vkplay_batch ? "table-info" : ""}>
                                <td>{obj.date_added}</td>
                                <td>{t("Бессрочно")}</td>
                                <td>{obj.id}</td>
                                <td>{MarketSerialBatchSerialTypeTrans[obj.serial_type]}</td>
                                <td>{obj.amount}</td>
                                <td className="show-wrap-text">
                                    {vkplay_batch ? (
                                        commonInfo.account.is_super ? (
                                            <OverflowArea y={150}>{obj.comment}</OverflowArea>
                                        ) : (
                                            t("Для маркетинговых нужд VK Play")
                                        )
                                    ) : (
                                        <OverflowArea y={150}>{obj.comment}</OverflowArea>
                                    )}
                                </td>
                                <td>{vkplay_batch && !commonInfo.account.is_super ? "VK Play" : obj.author_email}</td>
                                <td className="dmg-table-row-tools">
                                    {commonInfo.account.is_super ? (
                                        <MarketSerialBatchStateDisplay batch={obj} />
                                    ) : (
                                        !vkplay_batch && <MarketSerialBatchStateDisplay batch={obj} />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
}

function BatchGeneration({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:serials:generation", { game_id: gameId }));
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (!apiData) return;

    const disabled =
        gameCommonInfo.game.publication_state !== GamePublicationState.DONE &&
        gameCommonInfo.game.publication_wishlist_state !== GamePublicationWishlistState.DONE &&
        !commonInfo.account.is_super;

    const buttons = (
        <DevForm.Button variant="primary" type="submit" name="generate" disabled={disabled}>
            {t("Сгенерировать")}
        </DevForm.Button>
    );

    return (
        <CommonCard header={header}>
            {disabled && (
                <Alert variant="danger">
                    {t(
                        "Функционал генерации ключей будет доступен после прохождения модерации " +
                            "проекта и его публикации на портале VK Play"
                    )}
                </Alert>
            )}
            <RenderForm backUrl={url_reverse("games:serials", { game_id: gameId })} buttons={buttons} />
        </CommonCard>
    );
}

function Ban({ batchId, gameId, header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:serials:ban", { game_id: gameId, batch_id: batchId }));
    const [showConfirmation, setShowConfirmation] = useState(false);
    const onFormSubmit = useSubmit();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const commonInfo = useRouteLoaderData("commonInfo");

    const [canBan, setCanBan] = useState(false);
    useEffect(() => {
        setCanBan(
            commonInfo.account?.own_developers_ids?.includes(gameCommonInfo.developer.id) ||
                gameCommonInfo.permissions.can_own ||
                commonInfo.account?.is_super
        );
    }, [commonInfo]);

    if (!apiData) return;

    if (!canBan) {
        return <CommonCard header={header}>{t('Функционал доступен для аккаунтов с ролью "Владелец"')}</CommonCard>;
    }

    const confirmationClose = () => {
        setShowConfirmation(false);
    };

    return (
        <>
            <CommonCard header={header}>
                <Form>
                    <RenderForm bareForm={true} />
                    <DevForm.Button variant="primary" name="save" onClick={() => setShowConfirmation(true)}>
                        {t("Сохранить")}
                    </DevForm.Button>
                </Form>
                <DevModal
                    show={showConfirmation}
                    onHide={confirmationClose}
                    header={t("Подтверждение")}
                    body={t("Вы уверены? Процесс блокировки необратим")}
                    footer={
                        <DevForm.Button
                            variant="primary"
                            type="submit"
                            name="ban"
                            onClick={(ev) =>
                                onFormSubmit(ev, {
                                    backUrl: url_reverse("games:serials", { game_id: gameId }),
                                    onFinally: () => confirmationClose(),
                                })
                            }
                        >
                            {t("Подтверждаю")}
                        </DevForm.Button>
                    }
                    renderWrapper={(children) => <Form>{children}</Form>}
                />
            </CommonCard>

            {apiData.batches?.length > 0 && (
                <Card className="mt-3">
                    <Card.Header>{t("Партии")}</Card.Header>
                    <Card.Body>
                        <Table hover responsive className="dmg-table">
                            <thead>
                                <tr>
                                    <th>{t("ID партии")}</th>
                                    <th>{t("Дата")}</th>
                                    <th>{t("Логин")}</th>
                                    <th>{t("Комментарий")}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {apiData.batches?.map((o) => {
                                    return (
                                        <tr key={`batches${o.id}`}>
                                            <td>{o.id}</td>
                                            <td>{o.date_ban}</td>
                                            <td>{o.author_email_ban}</td>
                                            <td className="show-wrap-text">
                                                <OverflowArea y={150}>{o.comment_ban}</OverflowArea>
                                            </td>
                                            <td>
                                                <Awesome
                                                    type="file-excel-o"
                                                    color="green"
                                                    tooltip={o.comment_moderator}
                                                    onClick={() =>
                                                        getAttachement(
                                                            url_reverse(
                                                                "api:game:serials:batch_list",
                                                                { game_id: o.game_id },
                                                                { export_batch: o.id }
                                                            )
                                                        )
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {apiData.serials?.length > 0 && (
                <Card className="mt-3">
                    <Card.Header>{t("Серийники")}</Card.Header>
                    <Card.Body>
                        <Table hover responsive className="dmg-table">
                            <thead>
                                <tr>
                                    <th>{t("Серийник")}</th>
                                    <th>{t("Дата")}</th>
                                    <th>{t("Логин")}</th>
                                    <th>{t("Комментарий")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiData.serials?.map((o) => {
                                    return (
                                        <tr key={`serials${o.id}`}>
                                            <td>{o.serial}</td>
                                            <td>{o.date_ban}</td>
                                            <td>{o.author_email_ban}</td>
                                            <td className="show-wrap-text">{o.comment_ban}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
        </>
    );
}
